import { useState, useMemo } from "react";
import { formatDisplayDate, formatTime } from "../../utils/dateUtils";
import { getImageUrl } from "../../utils/imageUtils";
import { db } from "../../utils/firebase";
import { addDoc, collection, query, where, getDocs } from "firebase/firestore";

const TrailDetailView = ({ trail, onMatch, onBack, source, user }) => {
  const [liked, setLiked] = useState(false);
  const [selectedDay, setSelectedDay] = useState("Saturday");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const shareUrl = `${window.location.origin}/trail/${trail.id}`;
  const [copied, setCopied] = useState(false);

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 40000); // desaparece en 40 segundos

  } catch (err) {
    console.error("Failed to copy:", err);
  }
};

  const handleLikeClick = () => setLiked(!liked);

  const handleMapClick = () => {
    if (trail?.linkmap) {
      window.open(trail.linkmap, "_blank");
    }
  };

  const getButtonText = () => {
    switch (source) {
      case "agenda":
        return "Back to Agenda";
      case "matches":
        return "Back to Matches";
      default:
        return "Back to Home";
    }
  };

  const handleFindBuddy = async () => {
    try {
      if (!user) {
        if (typeof window !== "undefined" && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent("open-login-modal"));
        }
        return;
      }

      const matchesRef = collection(db, "matches");
      const q = query(
        matchesRef,
        where("profile_ids.id1", "==", user.uid),
        where("trail_id", "==", trail.id),
        where("status", "==", "searching")
      );

      const existingMatchesSnapshot = await getDocs(q);
      if (!existingMatchesSnapshot.empty) {
        console.log("A 'searching' match already exists. Skipping creation.");
        onMatch(trail);
        return;
      }

      const startTime = calculateNextWeekendDate(selectedDay);
      const endTime = new Date(startTime);
      endTime.setHours(startTime.getHours());

      await addDoc(collection(db, "matches"), {
        profile_ids: { id1: user.uid, id2: null },
        participants: [user.uid],
        status: "searching",
        trail_id: trail.id,
        start_time: startTime,
        end_time: endTime,
        compatibility: null,
        feedback: {},
        createdAt: new Date(),
      });

      onMatch(trail);
    } catch (err) {
      console.error("Error creating match:", err);
      alert("Something went wrong while trying to create a match.");
    }
  };

  const calculateNextWeekendDate = (day) => {
    const today = new Date();
    const currentDay = today.getDay();
    const targetDay = day === "Sunday" ? 0 : 6;
    let daysUntil = (targetDay + 7 - currentDay) % 7;
    if (daysUntil === 0) daysUntil = 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntil);
    nextDate.setHours(10, 0, 0, 0);
    return nextDate;
  };

  const displayedDate = useMemo(() => {
    const date = calculateNextWeekendDate(selectedDay);
    return new Date(date);
  }, [selectedDay]);

  return (
    <div className="min-h-screen bg-white-50 font-roboto">
      <div className="relative">
        <img
          src={getImageUrl(trail?.image)}
          alt={`View of ${trail?.name}`}
          className="w-full md:max-w-2xl h-64 object-cover mx-auto"
        />
        <button
          onClick={onBack}
          className="absolute top-5 left-4 bg-white/70 rounded-full p-2 shadow-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 text-gray-700"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
        </button>
        <button
          onClick={handleLikeClick}
          className="absolute top-5 right-4 bg-white/70 rounded-full p-2 shadow-md w-9 h-9 flex items-center justify-center"
        >
          <i
            className={`fas fa-heart ${
              liked ? "text-red-500" : "text-gray-700"
            }`}
          ></i>
        </button>
      </div>

      <main className="px-6 py-4 bg-white rounded-t-3xl -mt-6 relative max-w-4xl mx-auto md:px-12 lg:px-24">
        <h1 className="text-2xl font-bold mb-2 text-gradient bg-gradient-to-r from-[#6B01D1] to-[#F2787A] bg-clip-text text-transparent">
          {trail?.name}
        </h1>
        <p className="text-gray-600 mb-4">{trail?.location}</p>

      <button
        onClick={handleCopy}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-full hover:bg-purple-100 transition-all"
      >
        <i className="fas fa-share-alt"></i>
        {copied ? "Copied ✓" : "Copy link"}
      </button>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-6 items-center mb-2">
          <div className="flex flex-col items-center mt-5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6 text-black-600 mb-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25M3 18.75A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008Z"
              />
            </svg>
            <p className="text-gray-700 text-sm text-center">
              {formatDisplayDate(displayedDate)}
            </p>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="mt-1 text-sm border px-2 py-1 rounded"
            >
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </div>

          <div className="flex flex-col items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6 text-black-600 mb-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <p className="text-gray-700 text-sm text-center">
              {formatTime(trail.start_time)}
            </p>
          </div>

          <div className="flex flex-col items-center">
            <i className="fas fa-hiking text-xl mb-1"></i>
            <p className="text-gray-700 text-sm text-center capitalize">
              {trail?.activity || "Hiking"}
            </p>
          </div>

          <div className="flex flex-col items-center">
            <i className="fas fa-map-marked-alt text-xl mb-1"></i>
            <p className="text-gray-700 text-sm text-center">
              {trail?.length}
            </p>
          </div>

          <div className="flex flex-col items-center">
            <i className="fas fa-tachometer-alt text-xl mb-1"></i>
            <p className="text-gray-700 text-sm text-center">
              {trail?.difficulty}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-xl font-bold text-gray-800 mb-1">Meeting point</h2>
          <p className="text-sm text-gray-500 mb-2 mt-1">
            Tap in the map to view the exact meeting point
          </p>
          <div onClick={handleMapClick} className="cursor-pointer">
            <img
              src={getImageUrl(trail?.map)}
              alt={`Map showing ${trail?.name} location`}
              className="w-full h-48 md:h-64 lg:h-72 object-cover rounded-xl shadow-md transition-opacity duration-300 hover:opacity-90"
            />
          </div>
        </div>

        <div className="text-gray-700 mt-6">
          <p className={`${showFullDescription ? "" : "line-clamp-3"} transition-all`}>
            {trail?.description}
          </p>
          {trail?.description?.length > 200 && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="text-sm text-purple-600 mt-2 hover:underline focus:outline-none"
            >
              {showFullDescription ? "Read Less" : "Read More"}
            </button>
          )}
        </div>


        <div className="mt-8 space-y-4 mb-4">
          {source === "home" ? (
        <div className="flex justify-center">
          <button
            onClick={handleFindBuddy}
            className="w-full sm:w-3/4 md:w-2/3 lg:w-1/2 bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full py-3 font-semibold text-lg transition-transform duration-300 hover:scale-105 hover:shadow-xl"
          >
            Find Sports Buddy
          </button>
        </div>
          ) : (
            <button
              onClick={onBack}
              className="w-full md:w-3/4 lg:w-1/2 mx-auto bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full py-3 font-semibold text-lg transition-transform duration-300 hover:scale-105 hover:shadow-xl"
            >
              {getButtonText()}
            </button>
          )}
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-md">
        <div className="flex justify-around p-4 max-w-4xl mx-auto">
          <button className="flex flex-col items-center text-gray-400 hover:text-purple-600 transition-colors duration-300">
            <i className="fas fa-calendar-alt"></i>
            <p>Agenda</p>
          </button>
          <button className="flex flex-col items-center text-purple-600 hover:text-gray-700 transition-colors duration-300">
            <i className="fas fa-home"></i>
            <p>Home</p>
          </button>
          <button className="flex flex-col items-center text-gray-400 hover:text-purple-600 transition-colors duration-300">
            <i className="fas fa-sync-alt"></i>
            <p>Matches</p>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default TrailDetailView;
